import { useCallback } from 'react';
import { message } from 'antd';
import { 
  canPerformAIOperation, 
  recordAIOperation
} from '../services/usageTrackingSimple';
import { useUsageContext } from '../contexts/UsageContext';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface UseUsageTrackingReturn {
  usageStatus: any;
  loading: boolean;
  canUse: boolean;
  remaining: number;
  used: number;
  limit: number;
  refreshUsage: () => Promise<void>;
  checkAndRecordUsage: () => Promise<boolean>;
  showUsageExceededMessage: () => void;
}

/**
 * 使用次数跟踪Hook
 * 提供AI操作次数限制的相关功能
 */
export const useUsageTracking = (): UseUsageTrackingReturn => {
  const { t, i18n } = useTranslation();
  const { usageStatus, loading, refreshUsage } = useUsageContext();

  // 检查是否可以执行AI操作并记录使用
  const checkAndRecordUsage = useCallback(async (): Promise<boolean> => {
    try {
      // 先检查是否可以执行
      const canUse = await canPerformAIOperation();
      if (!canUse) {
        showUsageExceededMessage();
        return false;
      }

      // 记录使用
      const recorded = await recordAIOperation();
      if (recorded) {
        // 刷新全局状态
        await refreshUsage();
        return true;
      } else {
        showUsageExceededMessage();
        return false;
      }
    } catch (error) {
      logger.error('检查和使用记录失败:', error);
      message.error(
        i18n.language === 'zh-CN' 
          ? '操作失败，请稍后重试' 
          : 'Operation failed, please try again later'
      );
      return false;
    }
  }, [refreshUsage, i18n.language]);

  // 显示使用次数超限消息
  const showUsageExceededMessage = useCallback(() => {
    message.warning({
      content: i18n.language === 'zh-CN' 
        ? '今日AI操作次数已用完，请明天再试' 
        : 'Daily AI operations limit reached. Please try again tomorrow.',
      duration: 5,
      style: {
        marginTop: '20px',
      },
    });
  }, [i18n.language]);


  return {
    usageStatus,
    loading,
    canUse: usageStatus?.canUse || false,
    remaining: usageStatus?.remaining || 0,
    used: usageStatus?.used || 0,
    limit: usageStatus?.limit || 10,
    refreshUsage,
    checkAndRecordUsage,
    showUsageExceededMessage
  };
};

export default useUsageTracking;
